import moment from 'moment';
import { combineReducers } from 'redux';

import multiPartyLoadingStateReducerFactory from '@nhl/multi-party-loading-state-reducer-factory';
import { constants } from '@nhl/formatter-utilities';

import actionTypes from './actionTypes';

/* State Shape
 gameList: {
     loadingState: {
         partiesLoading: [],
         successByParty: {},
         errorsByParty: {}
     }
     gameListState: {
        gameData: Array,
        headerGameData: Array,
        boxscoreFeedData: Array,
        videoData: {
            recap: Object,
            goalVideos: []
        }
        retainBoxscoreFeedDataId: int
     }
 }
 */

const loadingStateReducer = multiPartyLoadingStateReducerFactory(
    actionTypes.REQUEST_GAME_DATA, actionTypes.REQUEST_BOXSCORE_DATA, actionTypes.REQUEST_GAME_DATE)(
        actionTypes.GAME_DATA_RESPONSE, actionTypes.BOXSCORE_DATA_RESPONSE, actionTypes.GAME_DATE_RESPONSE);

const combineAndDedupeGameData = (state, action) => {
    const combinedData = [...action.payload.gameData, ...state];
    let dedupedResults = [];

    // If we're on the boxscore page we want to retain boxscore data for a game with specified id,
    // so we have to do a bit more complex version of the deduping below where we do the normal
    // step of only adding items once to dedupedResults, but if any item contains boxscore data and
    // matches the retainBoxscoreId, we keep that item over everything
    if (action.payload.retainBoxscoreFeedDataId) {
        combinedData.forEach((game) => {
            const gIdx = dedupedResults.findIndex(g => g.id === game.id);
            if (gIdx !== -1) {
                const g = dedupedResults[gIdx];

                if (g.id === action.payload.retainBoxscoreFeedDataId && g.hfdEvents === undefined && game.hfdEvents) {
                    dedupedResults.splice(gIdx, 1, game);
                }
            } else {
                dedupedResults.push(game);
            }
        });
    } else {
        // This dedupes the list and then sorts the resulting list by game date and game id
        dedupedResults = [...action.payload.gameData, ...state].filter((game, idx, self) =>
            self.findIndex(g => g.id === game.id) === idx
        );
    }

    return dedupedResults.sort((a, b) => {
        const dateDiff = moment(a.gameDate, constants.DATE_FORMAT).diff(moment(b.gameDate, constants.DATE_FORMAT));
        const stateDiff = b.state.id - a.state.id;
        const timeDiff = moment(a.easternStartTime, constants.START_TIME_FORMAT).diff(moment(b.easternStartTime, constants.START_TIME_FORMAT));
        const idDiff = a.id - b.id;

        // Sort by game date, game state, start time, and id
        return dateDiff === 0 ? (stateDiff === 0 ? (timeDiff === 0 ? idDiff : timeDiff) : stateDiff) : dateDiff;
    });
}
const gameDataReducer = (state = [], action) => {
    switch (action.type) {
        case actionTypes.PURGE_GAME_DATA:
            return [];
        case actionTypes.GAME_DATA_RESPONSE:
            return combineAndDedupeGameData(state, action);
        default:
            return state;
    }
};

// we've separated this from the main gameDataReducer because there were cases where the game data used for the header
// was interfering with fetching and parsing the game data used for the rest of the games page
const headerGameDataReducer = (state = [], action) => {
    switch (action.type) {
        case actionTypes.HEADER_GAME_DATA_RESPONSE:
            return combineAndDedupeGameData(state, action);
        default:
            return state;
    }
};

const boxscoreFeedDataReducer = (state = [], action) => {
    switch (action.type) {
        case actionTypes.BOXSCORE_DATA_RESPONSE:
            // This dedupes the list and then sorts the resulting list by game date and game id
            return [...action.payload.boxscoreFeedData, ...state].filter((boxscore, idx, self) =>
                self.findIndex(b => b.gameId === boxscore.gameId) === idx
            ).sort((a, b) => {
                const dateDiff = moment(a.boxscoreFeedData, constants.DATE_FORMAT).diff(moment(b.boxscoreFeedData, constants.DATE_FORMAT));

                return dateDiff === 0 ? a.id - b.id : dateDiff;
            });
        default:
            return state;
    }
};

const retainBoxscoreFeedDataIdReducer = (state = null, action) => {
    if (action.type === actionTypes.SET_RETAIN_BOXSCORE_DATA_ID) {
        return action.payload.retainBoxscoreFeedDataId;
    }
    return state;
};

const gameDateReducer = (state = null, action) => {
    if (action.type === actionTypes.GAME_DATE_RESPONSE) {
        return action.payload.gameDate;
    }
    return state;
};

const summaryReducer = (state = null, action) => {
    if (action.type === actionTypes.LANDING_DATA_RESPONSE) {
        return action.payload.summary || null;
    }
    return state;
};

const landingDataReducer = combineReducers({
    summary: summaryReducer
});

const gameInfoReducer = (state = null, action) => {
    if (action.type === actionTypes.RIGHT_RAIL_DATA_RESPONSE) {
        return action.payload.gameInfo || null;
    }
    return state;
};

const rightRailDataReducer = combineReducers({
    gameInfo: gameInfoReducer
});

const gameListStateReducer = combineReducers({
    gameData: gameDataReducer,
    headerGameData: headerGameDataReducer,
    boxscoreFeedData: boxscoreFeedDataReducer,
    retainBoxscoreFeedDataId: retainBoxscoreFeedDataIdReducer,
    gameDate: gameDateReducer,
    landingData: landingDataReducer,
    rightRailData: rightRailDataReducer
});

const reducer = combineReducers({
    loadingState: loadingStateReducer,
    gameListState: gameListStateReducer
});

export default reducer;
